import { useCallback, useEffect, useRef } from "react";
import type { StrapiMedia } from "../types/cms";
import { mediaUrl } from "../lib/strapi";

/**
 * Lightbox accesible para una galería de imágenes.
 *
 *  - Cierra con Escape o clic en el fondo / botón ✕.
 *  - Navega con flechas izquierda/derecha y con los botones laterales.
 *  - role="dialog" + aria-modal="true". Mueve el foco al botón de cierre
 *    al abrir y lo devuelve al elemento que abrió el lightbox al cerrar.
 *  - Se queda dentro del componente: el grid de thumbnails es quien
 *    decide cuándo abrirlo y qué índice mostrar.
 *
 * Sin dependencias — el sitio ya carga framer-motion para otras cosas
 * pero esto es lo bastante simple como para no necesitarlo.
 */
export function Lightbox({
  fotos,
  index,
  onClose,
  onIndexChange,
}: {
  fotos: StrapiMedia[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  const total = fotos.length;
  const safeIndex = Math.max(0, Math.min(index, total - 1));
  const foto = fotos[safeIndex];

  const irAnterior = useCallback(() => {
    onIndexChange(safeIndex === 0 ? total - 1 : safeIndex - 1);
  }, [safeIndex, total, onIndexChange]);

  const irSiguiente = useCallback(() => {
    onIndexChange((safeIndex + 1) % total);
  }, [safeIndex, total, onIndexChange]);

  // Focus management + bloqueo de scroll del body mientras está abierto.
  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      previousFocus.current?.focus?.();
    };
  }, []);

  // Atajos de teclado.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        irAnterior();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        irSiguiente();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, irAnterior, irSiguiente]);

  if (!foto) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={
        foto.alternativeText
          ? `Imagen ${safeIndex + 1} de ${total}: ${foto.alternativeText}`
          : `Imagen ${safeIndex + 1} de ${total}`
      }
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/90 p-4"
      onClick={(e) => {
        // Cerrar al pinchar el fondo (no al pinchar la imagen ni los botones).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Cerrar */}
      <button
        ref={closeBtnRef}
        type="button"
        onClick={onClose}
        aria-label="Cerrar visor de imágenes"
        className="absolute right-4 top-4 z-10 rounded-full bg-brand-paper/90 px-4 py-2 text-sm font-semibold text-brand-ink shadow-md transition hover:bg-brand-orange hover:text-brand-paper focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
      >
        ✕ Cerrar
      </button>

      {/* Anterior */}
      {total > 1 && (
        <button
          type="button"
          onClick={irAnterior}
          aria-label="Imagen anterior"
          className="absolute left-2 z-10 rounded-full bg-brand-paper/90 p-3 text-brand-ink shadow-md transition hover:bg-brand-orange hover:text-brand-paper focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 md:left-6"
        >
          <span aria-hidden="true">←</span>
        </button>
      )}

      {/* Imagen */}
      <figure className="flex max-h-full max-w-5xl flex-col items-center">
        <img
          src={mediaUrl(foto.url)}
          alt={foto.alternativeText ?? ""}
          className="max-h-[80vh] w-auto max-w-full rounded-lg object-contain shadow-2xl"
        />
        <figcaption className="mt-4 max-w-3xl text-center text-sm text-brand-paper">
          {foto.alternativeText && <p>{foto.alternativeText}</p>}
          <p className="mt-1 text-xs text-brand-paper/70">
            {safeIndex + 1} / {total}
          </p>
        </figcaption>
      </figure>

      {/* Siguiente */}
      {total > 1 && (
        <button
          type="button"
          onClick={irSiguiente}
          aria-label="Imagen siguiente"
          className="absolute right-2 z-10 rounded-full bg-brand-paper/90 p-3 text-brand-ink shadow-md transition hover:bg-brand-orange hover:text-brand-paper focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 md:right-6"
        >
          <span aria-hidden="true">→</span>
        </button>
      )}
    </div>
  );
}
